import { useState } from "react";
import { Dropdown, MenuProps, Tooltip, message } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { showFallbackErrorToast } from "@apis/http/httpErrorToast";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import { themeAtom } from "@src/jotai/theme";
import { IconMore } from "@icons/index";
import Image from "@components/Image";
import { DangerConfirmModal } from "@components/DangerConfirmModal";
import { CreateCharacterEntityModal } from "@pages/library/CreateCharacterEntityModal";
import { CharacterAssetCreateFormRequestCategory } from "@apis/generated/openapi";
import { deleteCharacterAsset, type CharacterAssetRecord } from "@apis/http/api";
import { UIProductSubImages } from "./UIProductSubImages";

interface UICharacterCardProps {
  record: CharacterAssetRecord;
  onRefresh?: () => void;
}

/**
 * Library > Characters 列表卡。
 *
 * 设计稿节点：3352:32081 / 3352:32104。
 *
 * - 大方图展示主图
 * - 卡片底部显示 character name
 * - hover 展示 More 菜单（Edit / Delete），编辑走 CreateCharacterEntityModal
 * - 根据 bindState 在右上角徽标位置展示 BINDING/FAILED 状态，FAILED 可 hover 看错误信息
 */
export const UICharacterCard = ({ record, onRefresh }: UICharacterCardProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isDark = useAtomValue(themeAtom) === "dark";

  const handleCardClick = () => {
    setEditOpen(true);
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteCharacterAsset({ assetId: record.id });
      message.success(t("Character deleted"));
      setConfirmOpen(false);
      onRefresh?.();
    } catch (err: any) {
      showFallbackErrorToast(err, t("Failed to delete character"));
    } finally {
      setDeleting(false);
    }
  };

  const items: MenuProps["items"] = [
    {
      key: "delete",
      label: (
        <div
          className="w-full h-full flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(true);
          }}
        >
          <Trans>Delete</Trans>
        </div>
      ),
    },
  ];

  const bindState = record.bindState;
  const errorMessage = record.platformSummary?.errorMessage;

  return (
    <>
      <div
        key={record.id}
        className="bg-kds-bg border border-kds-border rounded-[8px] overflow-hidden h-[327px] w-full relative group cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="relative aspect-square w-full h-full z-1">
          <Image className="w-full h-full rounded-none! border-none!" url={record.mainImage?.imageUrl} />
          <UIProductSubImages
            images={(record.otherImages ?? []).map((img) => ({ url: img.imageUrl }))}
            className="hidden group-hover:flex"
          />
        </div>
        <div className="px-[15px] pt-[15px] pb-[12px] text-kds-fg text-[14px] leading-normal line-clamp-2">
          {record.name}
        </div>

        {bindState === "BINDING" && (
          <div className="absolute left-[12px] top-[12px] px-[8px] h-[22px] flex items-center rounded-[4px] bg-kds-mask text-white text-[12px]">
            <Trans>Processing</Trans>
          </div>
        )}
        {bindState === "FAILED" && (
          <Tooltip title={errorMessage || t("Binding failed. Please retry.")}>
            <div className="absolute left-[12px] top-[12px] px-[8px] h-[22px] flex items-center rounded-[4px] bg-kds-danger-fg text-white text-[12px]">
              <Trans>Failed</Trans>
            </div>
          </Tooltip>
        )}

        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown trigger={["hover"]} menu={{ items }} placement="bottomLeft" onOpenChange={setMenuOpen}>
            <div
              className={classNames(
                "absolute right-[12px] top-[12px] w-[24px] h-[24px] cursor-pointer items-center justify-center rounded-[4px] hidden group-hover:flex",
                isDark ? "bg-kds-mask" : "bg-[#22222240]",
                { "flex!": menuOpen },
              )}
            >
              <IconMore className="w-[16px] h-[16px] text-white" />
            </div>
          </Dropdown>
        </div>
      </div>

      <CreateCharacterEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        characterId={record.id}
        defaultCategory={
          (record.category as unknown as CharacterAssetCreateFormRequestCategory) ??
          CharacterAssetCreateFormRequestCategory.MODEL
        }
        onSuccess={onRefresh}
      />

      <DangerConfirmModal
        open={confirmOpen}
        title={<Trans>Delete this character?</Trans>}
        description={
          <Trans>
            This will remove the character from your library. Videos already generated with this character will not be
            affected.
          </Trans>
        }
        confirmText={<Trans>Delete</Trans>}
        confirmLoading={deleting}
        onConfirm={handleDelete}
        onCancel={() => {
          if (!deleting) setConfirmOpen(false);
        }}
      />
    </>
  );
};
